import { useCallback, useContext, useEffect, useMemo } from 'react';
import { Layer, type MapLayerMouseEvent, Source } from 'react-map-gl';
import { Feature, Point } from 'geojson';
import { AreaSearchResult } from '@alltrails/search/types/algoliaResultTypes';
import { useDispatch, useSelector } from '../../redux';
import { serializedCoordinatesToCoordinates } from '../../utils/serializedCoordinates';
import useSvgImages, { SvgDefinition } from '../../utils/useSvgImages';
import { defaultParkPinLayerLayoutProps, defaultSymbolLayerLayoutProps, getLayerBeforeId, parkPinsId } from '../../utils/layers';
import useMap from '../../hooks/useMap';
import { updateActivePark, updateCursor } from '../../redux/reducer';
import { MapLayerInteractionsContext } from '../MapLayerInteractionsProvider';
import nationalParkPinSvg from '../../utils/svgDefinitions/nationalParkPinSvg';
import defaultParkPinSvg from '../../utils/svgDefinitions/defaultParkPinSvg';
import ParkResultProperties from './ParkResultProperties';

const svgDefinitions: SvgDefinition[] = [nationalParkPinSvg, defaultParkPinSvg];

const sourceId = 'park-results-source';

export type ParkResultsProps = {
  onParkClick?: (park: AreaSearchResult) => void;
};

const ParkResults = ({ onParkClick }: ParkResultsProps) => {
  const map = useMap();
  const dispatch = useDispatch();
  const { activePark, allowClickEvents, parkResults } = useSelector(state => ({
    activePark: state.map.activePark,
    allowClickEvents: state.map.allowClickEvents,
    parkResults: state.map.parkResults
  }));

  const { registerEventHandler, unregisterEventHandler } = useContext(MapLayerInteractionsContext);

  const { imagesAreLoaded } = useSvgImages(svgDefinitions);

  const features = useMemo(() => {
    if (!parkResults) {
      return [];
    }
    return Object.keys(parkResults).reduce<Feature<Point, ParkResultProperties>[]>((allFeatures, serializedCoordinates) => {
      const park = parkResults[serializedCoordinates];
      if (!park || !park.area_type) {
        return allFeatures;
      }
      const { lat, lng } = serializedCoordinatesToCoordinates(serializedCoordinates);
      return [
        ...allFeatures,
        {
          geometry: { type: 'Point', coordinates: [lng, lat] },
          properties: { areaType: park.area_type, serializedCoordinates },
          type: 'Feature'
        }
      ];
    }, []);
  }, [parkResults]);

  const onClick = useCallback(
    (event: MapLayerMouseEvent) => {
      if (event.features) {
        const marker = event.features[0] as unknown as Feature<Point, ParkResultProperties>;
        const park = parkResults?.[marker.properties.serializedCoordinates];
        if (park && park.ID !== activePark?.ID) {
          onParkClick?.(park);
          dispatch(updateActivePark(park));
        }
      }
    },
    [activePark?.ID, dispatch, onParkClick, parkResults]
  );

  const onMouseMove = useCallback(() => {
    dispatch(updateCursor('pointer'));
  }, [dispatch]);

  const onMouseLeave = useCallback(() => {
    dispatch(updateCursor('unset'));
  }, [dispatch]);

  useEffect(() => {
    if (!allowClickEvents) {
      return;
    }

    registerEventHandler(parkPinsId, 'click', onClick);
    registerEventHandler(parkPinsId, 'mousemove', onMouseMove);
    registerEventHandler(parkPinsId, 'mouseleave', onMouseLeave);
    return () => {
      unregisterEventHandler(parkPinsId, 'click', onClick);
      unregisterEventHandler(parkPinsId, 'mousemove', onMouseMove);
      unregisterEventHandler(parkPinsId, 'mouseleave', onMouseLeave);
    };
  }, [allowClickEvents, onClick, onMouseLeave, onMouseMove, registerEventHandler, unregisterEventHandler]);

  if (!imagesAreLoaded || !features.length) {
    return null;
  }

  return (
    <Source id={sourceId} type="geojson" data={{ type: 'FeatureCollection', features }}>
      <Layer
        type="symbol"
        id={parkPinsId}
        source={sourceId}
        beforeId={getLayerBeforeId(map, parkPinsId)}
        layout={{
          ...defaultSymbolLayerLayoutProps,
          ...defaultParkPinLayerLayoutProps,
          'icon-image': ['case', ['==', ['get', 'areaType'], 'N'], nationalParkPinSvg.name, defaultParkPinSvg.name]
        }}
      />
    </Source>
  );
};

export default ParkResults;
